(ns manatee.admin.view
  (:require
   [cljs-time.core :as time]
   [clojure.string :as string]
   [kee-frame.core :as kf]
   [manatee.admin.app-db :as admin-db]
   [manatee.admin.components :as admin-components]
   [manatee.admin.handlers :as handlers]
   [manatee.admin.pending-items.components :as pending-items-components]
   [manatee.admin.queue.components :as admin-queue-components]
   [manatee.admin.rejection-option.components :as rejection-components]
   [manatee.admin.rejection-option.subs :as rejection-subs]
   [manatee.admin.subs :as subs]
   [manatee.admin.task-config.subs :as task-config-subs]
   [manatee.admin.users.components :as users-components]
   [manatee.components.notifications.container :as notifications]
   [manatee.components.table.formatters :as formatters]
   [manatee.domain.task :as task]
   [manatee.spec.core :as spec]
   [manatee.ui.subs :as ui-subs]
   [manatee.ui.templates :as templates]
   [manatee.user.subs :as user-subs]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [rothko.button.component :refer [button]]
   [rothko.collection-components.core :as collection]
   [rothko.collection-components.table.component :as table]
   [rothko.form2.component :as form]
   [rothko.form2.input.component :as input]
   [rothko.form2.select.core :as select]
   [rothko.form2.select.single-select.component :as single-select]
   [rothko.form2.validation :as validation]
   [rothko.layout.component :refer [layout]]
   [rothko.nav.component :refer [nav]]
   [rothko.panel.component :refer [panel]]))

(defn task-configs->select-options
  [task-configs]
  (->> task-configs
       (map (fn [{:keys [id data task-name]}]
              {:value id
               :representation (admin-queue-components/format-task-name (:account-id data) task-name)}))
       (sort-by :representation)))

(defn tab-component
  "Available icons are defined in Rothko rothko.icon.component/icon-names"
  [{:keys [selected-tab project-owner?] :as _props}]
  [nav {:appearance #{:horizontal}
        :menu-toggle-handler #(rf/dispatch [::handlers/select-tab %])
        :items [{:icon :user
                 :key :users
                 :selected? (= selected-tab :users)
                 :route [:users]
                 :title "Users"}
                {:icon :list-ul
                 :key :queue
                 :selected? (= selected-tab :queue)
                 :route [:queue]
                 :title "Queues"}
                {:icon :bars
                 :key :tasks
                 :selected? (= selected-tab :tasks)
                 :route [:tasks]
                 :title "Tasks"}
                (when-not project-owner?
                  {:icon :cross
                   :key :rejections
                   :selected? (= selected-tab :rejections)
                   :route [:rejections]
                   :title "Rejections"})
                (when-not project-owner?
                  {:icon      :message
                   :key       :messages
                   :selected? (= selected-tab :messages)
                   :route     [:messages]
                   :title     "Messages"})
                {:icon :history
                 :key :pending
                 :selected? (= selected-tab :pending)
                 :route [:pending]
                 :title "Pending"}
                (when-not project-owner?
                  {:icon :document-add
                   :key :user-guide
                   :selected? (= selected-tab :user-guide)
                   :route [:user-guide]
                   :title "User Guide"})]
        :route-handler (fn [[tab-key]]
                         (kf/path-for [::handlers/admin {:query-params {:tab (name tab-key)}}]))}])

(defn tasks-panel
  "Dsiplay the list of tasks, constrained if necessary for the current user's role."
  [_props]
  (rf/dispatch [::handlers/clear-selected-task]) ; https://btsservice.atlassian.net/browse/ELIT-4706
  (let [show-archived-tasks? (r/atom false)]
    (fn [{:keys [all-tasks active-tasks project-owner? user-teams] :as _props}]
      (let [team-value-formatter (fn [user-teams _ field]
                                   [:p {:style {:font-size :smaller}}
                                    (:representation (first (filter #(= (:value %)
                                                                        (:team-id field))
                                                                    user-teams)))])
            task-configs (if @show-archived-tasks? all-tasks active-tasks)
            selected-task-id (:id @(rf/subscribe [::task-config-subs/selected-task]))
            all-modifiers-maps (mapv (fn [tc]
                                       (get-in tc [:data :modifiers]))
                                     task-configs)
            all-modifiers-set (set (flatten (mapv keys all-modifiers-maps)))
            all-modifiers (sort (filter some? all-modifiers-set))
            task-configs-table (reduce (fn [result task-config]
                                         (let [account-id (get-in task-config [:data :account-id])
                                               task-type (some-> task-config task/task-type name)
                                               part-rejections (get-in task-config [:data :part :rejections :reasons])
                                               part-rejections-labels (filter some? (mapv :label part-rejections))
                                               answer-rejections (get-in task-config [:data :answer :rejections :reasons])
                                               answer-rejections-labels (filter some? (mapv :label answer-rejections))
                                               modifiers (get-in task-config [:data :modifiers])]
                                           (conj result {:task-id (:id task-config)
                                                         :task-name (:task-name task-config)
                                                         :account-id account-id
                                                         :task-type task-type
                                                         :part-rejections-labels part-rejections-labels
                                                         :answer-rejections-labels answer-rejections-labels
                                                         :all-modifiers all-modifiers
                                                         :modifiers modifiers
                                                         :selected? (= (:id task-config) selected-task-id)
                                                         :team-id (:team-id task-config)})))
                                       []
                                       (sort (fn [tc1 tc2]
                                               (compare (string/lower-case (:task-name tc1))
                                                        (string/lower-case (:task-name tc2))))
                                             task-configs))
            all-fields (cond-> [{::collection/label "Task"
                                 ::table/formatter (formatters/edit-task-url)}
                                {::collection/label "Type"
                                 ::table/formatter (formatters/smaller-p :task-type)}
                                {::collection/label "Account"
                                 ::table/formatter (formatters/smaller-p :account-id)}]
                         (not project-owner?) (conj {::collection/label "Team"
                                                     ::table/formatter (partial team-value-formatter user-teams)})
                         :always (conj {::collection/label "Source"
                                        ::table/formatter (formatters/account-source)}))]
        [:<>
         [panel {:appearance #{:primary}
                 :class [:admin-page__panel]}
          [layout {:appearance #{:column}
                   :class [:admin-page__table-layout]}
           [:h4 {:class [:admin-page__header]} "Tasks"]
           [:div.form-row.button-group
            [button {:appearance #{:tertiary}
                     :type :button
                     :on-click #(rf/dispatch [:navigate-to [:manatee.task-config.add.handlers/add-task]])}
             "New"]]
           [:div

            [:div {:class [:admin-page__form-items]}
             (if selected-task-id
               [button {:appearance #{:secondary}
                        :class [:admin-page__activate-button]
                        :on-click   #(rf/dispatch [::handlers/delete-task selected-task-id])}
                "Delete Task"]
               [:div])]

            [:div.checkbox__container
             {:class [:manatee]}
             [:input
              {:type           :checkbox
               :name           :show-inactive
               :on-change      #(swap! show-archived-tasks? not)
               :disabled       false
               :defaultChecked false}]
             [:label {:for :show-inactive} "Show archived tasks"]]

            [table/component
             {:appearance #{:primary}
              ::collection/items task-configs-table
              ::table/row-on-click #(do (rf/dispatch [::handlers/select-task (:task-id %)]))
              ::collection/fields all-fields}]]]]]))))

(defn team-filter
  [user-teams user-team-filter]
  [:div {:key (count user-teams)}
   [:label "Filter by team"]
   [single-select/field
    (merge
     (admin-components/form-field-props :no-team-filter #(do (rf/dispatch [::handlers/set-team-filter %]) (reset! user-team-filter %)))
     {::select/options (conj user-teams {:value :no-team-filter :representation "<No filter>"})})]])

(defn edit-message-component
  [{:keys [id message-text edit-start-time edit-end-time marked?] :as _selected}]
  [:div {:key [id marked?]}
   [form/form
    {::form/name      :messages-form
     ::form/on-submit #(rf/dispatch [::handlers/save-message id %])}
    {:message-text {::form/name           :message-text
                    ::form/label          "Enter message text"
                    ::form/initial-value  message-text
                    ::input/type          :textarea
                    ::validation/spec     ::spec/not-empty
                    ::validation/messages {::spec/not-empty "Must not be empty"}
                    :appearance           #{:admin-page__message-field}}
     :start-time   {::form/name          :start-time
                    ::form/label         "Enter message start time (GMT)"
                    ::input/type         :datetime
                    ::form/initial-value (or edit-start-time
                                             (admin-db/format-editable-time (time/now)))}
     :end-time     {::form/name          :end-time
                    ::form/label         "Enter message end time (GMT)"
                    ::input/type         :datetime
                    ::form/initial-value (or edit-end-time
                                             (admin-db/format-editable-time (time/plus (time/now) (time/days 1))))}}
    (fn [{:keys [::form/submit! ::validation/valid?]} {:keys [message-text start-time end-time] :as _field-props}]
      [:div
       [input/field message-text]
       [input/field start-time]
       [input/field end-time]
       [:div.form-row.button-group
        [button {:appearance #{:primary}
                 :class      [:admin-page__submit-button]
                 :id         :submit-btn
                 :on-click   submit!
                 :disabled   (not valid?)}
         (if id "Update" "Create")]
        (when id [button {:appearance #{:secondary}
                          :on-click   #(rf/dispatch [::handlers/toggle-message-mark id])}
                  (if marked? "Unmark message" "Mark for deletion")])
        (when marked? [button {:appearance #{:secondary}
                               :on-click   #(rf/dispatch [::handlers/purge-message])}
                       "Delete Marked Message!"])]])]])

(defn- format-marked
  [id]
  (fn [_ {:keys [marked?] :as msg}]
    (cond->> (get msg id)
      marked? (vector :s))))

(defn messages-panel
  [_]
  (fn [{:keys [selected-message] :as _props}]
    (let [messages @(rf/subscribe [::subs/messages])]
      [panel {:appearance #{:primary}
              :class      [:admin-page__panel]}
       [layout {:appearance #{:column}
                :class      [:admin-page__form-layout]}
        [:h4 {:class [:admin-page__header]} (if selected-message "Edit Message" "Create Message")]
        [:div
         (cond (empty? messages)
               ;; This special case ensures the form is cleared after saving.
               [:div {:class [:admin-page__forms]} [edit-message-component {}]]

               selected-message
               [:div {:class [:admin-page__form-items]}
                [edit-message-component selected-message]]

               :else
               [edit-message-component {}])]]
       [layout {:appearance #{:column}
                :class      [:admin-page__table-layout]}
        [:h4 {:class [:admin-page__header]} "Admin Messages"]
        [table/component
         {:appearance          #{:primary}
          ::collection/items   messages
          ::table/row-on-click #(rf/dispatch [::handlers/select-message (:id %)])
          ::collection/fields  [{::collection/id :disp-start-time ::collection/label "Start" ::table/formatter (format-marked :disp-start-time)}
                                {::collection/id :disp-end-time ::collection/label "Expiry" ::table/formatter (format-marked :disp-end-time)}
                                {::collection/id :message-text ::collection/label "Message" ::table/formatter (format-marked :message-text)}]}]]])))

(defn user-guide-panel
  []
  [panel {:appearance #{:primary}
          :class [:admin-page__panel]}
   [layout {:appearance #{:column}
            :class [:admin-page__form-layout]}
    [:h4 {:class [:admin-page__header]} "Upload a new Manatee User Guide PDF document"]
    [:div {:class [:admin-page__forms]}
     [admin-components/user-guide-upload-form-component]]]])

(defn admin-view
  [{:keys [selected-tab class] :as props}]
  [:div {:class class}
   [notifications/alerts {:banner-id :main
                          :max-alerts 1}]
   [tab-component props]
   (case selected-tab
     :users [users-components/users-panel props]
     :queue [admin-queue-components/queue-panel]
     :tasks [tasks-panel props]
     :rejections [rejection-components/rejections-panel props]
     :messages [messages-panel props]
     :pending [pending-items-components/pending-panel props]
     :user-guide [user-guide-panel])])

(defn access-denied
  [{:keys [class]}]
  (let [{:keys [base-url]} @(rf/subscribe [::ui-subs/config])]
    [panel {:appearance #{:primary}
            :class class}
     [layout {:appearance #{:column}
              :class [:admin-page__text-layout]}
      [:h4 {:class [:admin-page__header]} "You don't have permission to be here"]
      [:p "Only admin users can see this page. Click " [:a {:href base-url} "here"] " to go back."]]]))

(defn container
  [props]
  (let [selected-user @(rf/subscribe [::subs/selected-user])
        selected-message @(rf/subscribe [::subs/selected-message])
        selected-tab @(rf/subscribe [::subs/selected-tab])
        current-user @(rf/subscribe [::user-subs/user])
        users @(rf/subscribe [::subs/users])
        user-teams @(rf/subscribe [::subs/user-teams])
        active-users @(rf/subscribe [::subs/active-users])
        admin? @(rf/subscribe [::user-subs/admin?])
        project-owner? @(rf/subscribe [::user-subs/project-owner?])
        all-tasks @(rf/subscribe [::task-config-subs/task-configs])
        active-tasks @(rf/subscribe [::task-config-subs/active-task-configs])
        active-rejections @(rf/subscribe [::rejection-subs/active-rejection-options])
        all-rejections @(rf/subscribe [::rejection-subs/rejection-options])
        selected-rejection @(rf/subscribe [::rejection-subs/selected-rejection-option])]
    (if (or admin? project-owner?)
      [admin-view (merge props
                         {:selected-tab selected-tab
                          :current-user current-user
                          :project-owner? project-owner?
                          :users users
                          :user-teams user-teams
                          :active-users active-users
                          :selected-user selected-user
                          :selected-message selected-message
                          :all-tasks all-tasks
                          :active-tasks active-tasks
                          :all-rejections all-rejections
                          :active-rejections active-rejections
                          :selected-rejection selected-rejection})]
      [access-denied props])))

(def page
  {:main container
   :template templates/single-column})
